import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, Form, Spinner, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import {
  faFacebook,
  faLinkedin,
  faWhatsapp
} from '@fortawesome/free-brands-svg-icons';
import { TitleGenerator } from 'utils/TitleGenerator';
import messageHandler from 'utils/messageHandler';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { FiCheckCircle } from 'react-icons/fi';

const SendModal = ({ property, setShow, type }) => {
  const [loading, setLoading] = useState(false);
  const [isMailSent, setIsMailSent] = useState(false);

  const generateShareLink = () => {
    const url =
      type === 'project'
        ? `${process.env.REACT_APP_CLIENT_DETAIL_PROJECT}${
            property._id
          }/${TitleGenerator(property.title)}`
        : `${process.env.REACT_APP_CLIENT_DETAIL}${
            property._id
          }/${TitleGenerator(property.title)}`;

    return url;
  };

  const emailShareSchema = yup.object().shape({
    email: yup.string().email('Por favor ingrese un email válido.').required()
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(emailShareSchema)
  });

  const handleEmailShare = (data, e) => {
    e.preventDefault();
    e.stopPropagation();
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');

    const raw = JSON.stringify({
      property,
      email: data.email
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow'
    };

    setLoading(true);
    fetch(
      `${process.env.REACT_APP_SERVER}/send-property-from-admin`,
      requestOptions
    )
      .then(result => {
        console.log(result);
        setIsMailSent(true);
      })
      .catch(error => console.log('error', error))
      .finally(() => {
        setLoading(false);
        reset();
      });
  };

  const handleWhatsAppShare = () => {
    const text =
      '🏡 ¡Encontré una propiedad en CABAPROP que podría interesarte! Cualquier consulta estoy a tu disposición : ' +
      generateShareLink();
    window.open(
      'https://api.whatsapp.com/send?text=' + encodeURIComponent(text)
    );
  };

  const handleCopyLink = () => {
    const el = document.createElement('textarea');
    el.value = generateShareLink();
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    messageHandler('success', 'Enlace copiado al portapapeles');
  };

  const handleFacebookShare = () => {
    const url = generateShareLink();
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      url
    )}`;
    window.open(facebookUrl, 'Compartir en Facebook', 'width=600,height=400');
  };

  const handleLinkedInShare = () => {
    const url = generateShareLink();
    const linkedinUrl = `https://www.linkedin.com/shareArticle?url=${encodeURIComponent(
      url
    )}`;
    window.open(linkedinUrl, 'Compartir en LinkedIn', 'width=600,height=400');
  };

  return (
    <Modal
      keyboard={true}
      show={true}
      onHide={() => setShow(false)}
      backdrop="static"
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <FontAwesomeIcon
          icon="fa-solid fa-share-nodes"
          title="Eliminar"
          size="lg"
          className="me-2"
        />
        <Modal.Title>Comparte esta publicación con tu cliente</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p>
          Comparte esta publicación con tu cliente para que pueda verla en
          detalle desde el portal de CABAPROP.
        </p>
        <div className="mt-3">
          {/* Botón para copiar el enlace al portapapeles */}
          <button
            className="btn btn-info d-block w-100"
            onClick={handleCopyLink}
          >
            {/* icono copiar */}
            <FontAwesomeIcon icon="copy" className="me-2" />
            Copiar enlace
          </button>
        </div>
        <div className="row">
          <div className="mt-3">
            {/* Botón para compartir por WhatsApp */}
            <button
              className="btn btn-success d-block w-100" // Agregamos la clase w-100
              onClick={handleWhatsAppShare}
            >
              <FontAwesomeIcon size="xl" icon={faWhatsapp} className="me-2" />
              Compartir por WhatsApp
            </button>
          </div>
          <div className="mt-3">
            {/* Botón para compartir en Facebook */}
            <button
              className="btn btn-primary d-block w-100" // Agregamos la clase w-100
              onClick={handleFacebookShare}
            >
              <FontAwesomeIcon icon={faFacebook} className="me-2" />
              Compartir en Facebook
            </button>
          </div>
          <div className="mt-3">
            {/* Botón para compartir en LinkedIn */}
            <button
              className="btn btn-success d-block w-100" // Agregamos la clase w-100
              onClick={handleLinkedInShare}
            >
              <FontAwesomeIcon icon={faLinkedin} className="me-2" />
              Compartir en LinkedIn
            </button>
          </div>
          <div className="mt-3">
            {/* Botón para compartir por correo electrónico */}

            <Form noValidate onSubmit={handleSubmit(handleEmailShare)}>
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlInput1"
              >
                <Form.Label>Email</Form.Label>
                <Form.Control
                  {...register('email')}
                  type="email"
                  placeholder="name@example.com"
                  name="email"
                  isInvalid={!!errors.email}
                />
              </Form.Group>
              <Form.Control.Feedback type="invalid" tooltip>
                {errors.email && errors.email.message}
              </Form.Control.Feedback>
              {!loading ? (
                <button type="submit" className="btn btn-primary d-block w-100">
                  <FontAwesomeIcon icon="envelope" className="me-2" /> Compartir
                  por correo
                </button>
              ) : (
                <button
                  type="button"
                  disabled
                  className="btn btn-primary d-block w-100"
                >
                  <Spinner
                    size="sm"
                    animation="border"
                    role="status"
                    className="me-2"
                  />
                  <span>Enviando...</span>
                </button>
              )}
              {isMailSent && (
                <Badge bg="success">
                  <FiCheckCircle /> El mail se envió correctamente.
                </Badge>
              )}
            </Form>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

SendModal.propTypes = {
  property: PropTypes.object.isRequired,
  setShow: PropTypes.func.isRequired,
  setCheckChanges: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired
};

export default SendModal;
